import { jsx as _jsx } from "react/jsx-runtime";
/**
 * The Camera3D nodes of one canvas, by node path, across every scene it draws: the main scene and
 * each SubViewport's own. A camera preview finds its camera and what it holds here, whichever world
 * holds the camera, and a remount or a removal re-renders the reader.
 */
import { createContext, useContext, useEffect, useMemo, useSyncExternalStore, } from 'react';
/** One value per node path, with a listener for each change. */
class PathStore {
    byPath = new Map();
    listeners = new Set();
    /** Returns the removal, which leaves a newer value at the same path in place. */
    register(path, value) {
        this.byPath.set(path, value);
        this.notify();
        return () => {
            if (this.byPath.get(path) !== value)
                return;
            this.byPath.delete(path);
            this.notify();
        };
    }
    get(path) {
        return this.byPath.get(path) ?? null;
    }
    subscribe = (listener) => {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    };
    notify() {
        for (const listener of this.listeners)
            listener();
    }
}
const SceneCamerasContext = createContext(null);
SceneCamerasContext.displayName = 'SceneCamerasContext';
/** One per canvas. A portal keeps React context, so a SubViewport's cameras register here too. */
export function SceneCamerasProvider({ children }) {
    const cameras = useMemo(() => ({ objects: new PathStore(), resources: new PathStore() }), []);
    return _jsx(SceneCamerasContext.Provider, { value: cameras, children: children });
}
/** Registers the camera in `ref` under `path` while it is mounted. Outside a provider it does nothing. */
export function useRegisterSceneCamera(path, ref) {
    const objects = useContext(SceneCamerasContext)?.objects;
    useEffect(() => {
        const camera = ref.current;
        if (!objects || !camera)
            return undefined;
        return objects.register(path, camera);
    }, [objects, path, ref]);
}
/** Registers what the Camera3D at `path` holds. Outside a provider it does nothing. */
export function useRegisterSceneCameraResources(path, held) {
    const resources = useContext(SceneCamerasContext)?.resources;
    useEffect(() => resources?.register(path, held), [resources, path, held]);
}
const NO_SUBSCRIPTION = () => () => { };
function usePathValue(store, path) {
    return useSyncExternalStore(store?.subscribe ?? NO_SUBSCRIPTION, () => store && path ? store.get(path) : null);
}
/** The mounted camera at `path`, or null for no path, no such camera, or no provider. */
export function useSceneCamera(path) {
    return usePathValue(useContext(SceneCamerasContext)?.objects, path);
}
/** What the Camera3D at `path` holds, or null for no path, no such camera, or no provider. */
export function useSceneCameraResources(path) {
    return usePathValue(useContext(SceneCamerasContext)?.resources, path);
}
//# sourceMappingURL=sceneCameras.js.map